'use memo';

import { createFileRoute } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { useEffect, useState } from 'react';
import { z } from 'zod';

import ActionButton from '@/components/Button/ActionButton';
import { HStack, VStack } from '@/components/Layout';
import { Text } from '@/components/Typography/Text';
import { useCommonTranslation } from '@/hooks/useCommonTranslation';
import { logoutService } from '@/lib/auth/ory/service';
import { defaultNS, useTranslation } from '@/lib/i18n';
import {
  OAuth2ApiError,
  acceptLogoutRequest,
  getLogoutRequest,
  isSafeRedirectTo,
  waitForMinLoaderVisible,
} from '@/lib/oauth2';
import { navigateToUrl } from '@/utils/navigateToUrl';

import { OAuth2Card } from '../-components/OAuth2Card';
import { OAuth2ErrorCard } from '../-components/OAuth2ErrorCard';
import { OAuth2ProgressScreen } from '../-components/OAuth2ProgressScreen';
import { OAuth2Screen } from '../-components/OAuth2Screen';

const SearchSchema = z.object({
  logout_challenge: z.string().min(1).optional(),
});

export const Route = createFileRoute('/oauth2/logout/')({
  validateSearch: zodValidator(SearchSchema),
  component: OAuth2LogoutPage,
});

type LogoutInfo = Awaited<ReturnType<typeof getLogoutRequest>>;

/**
 * `/oauth2/logout` ルート (RP-Initiated Logout)。
 * `rpInitiated === false` は確認なしで即 accept、`true` のみ確認 UI を表示する。
 */
function OAuth2LogoutPage() {
  const { logout_challenge } = Route.useSearch();
  const { t } = useTranslation(defaultNS, { keyPrefix: 'pages.oauth2' });
  const tc = useCommonTranslation();
  const [info, setInfo] = useState<LogoutInfo | null>(null);
  const [asyncError, setAsyncError] = useState<unknown>(null);
  const [submitting, setSubmitting] = useState(false);

  const missingChallengeError = !logout_challenge
    ? new Error('missing_logout_challenge')
    : null;
  const error = missingChallengeError ?? asyncError;
  const challenge = logout_challenge ?? '';

  const handleRedirect = (redirectTo: string) => {
    if (!isSafeRedirectTo(redirectTo)) {
      setAsyncError(new Error('unsafe_redirect_to'));
      return;
    }
    navigateToUrl(redirectTo);
  };

  /** Kratos セッションを破棄して Hydra logout を accept → redirect。 */
  const runLogoutFlow = async () => {
    // 1. Kratos のセッション破棄（失敗しても Hydra accept は試みる）
    try {
      const flow = await logoutService.initFlow();
      if (flow.logout_token) {
        await logoutService.submitFlow(flow.logout_token);
      }
    } catch {
      // Kratos logout 失敗時も Hydra accept は継続
    }
    // 2. Hydra accept
    const { redirectTo } = await acceptLogoutRequest({ challenge });
    handleRedirect(redirectTo);
  };

  const handleConfirm = async () => {
    setSubmitting(true);
    try {
      await runLogoutFlow();
    } catch (e) {
      setAsyncError(e);
    } finally {
      setSubmitting(false);
    }
  };

  // 初回ロード + rpInitiated === false なら即 accept
  useEffect(() => {
    if (!logout_challenge) return;
    let cancelled = false;
    void (async () => {
      const startedAt = performance.now();
      try {
        const data = await getLogoutRequest(logout_challenge);
        if (cancelled) return;
        if (!data.rpInitiated) {
          // Kratos セッション破棄 → Hydra accept を直列実行
          try {
            const flow = await logoutService.initFlow();
            if (flow.logout_token) {
              await logoutService.submitFlow(flow.logout_token);
            }
          } catch {
            // Kratos logout 失敗時も Hydra accept は継続
          }
          if (cancelled) return;
          const { redirectTo } = await acceptLogoutRequest({
            challenge: logout_challenge,
          });
          if (cancelled) return;
          if (!isSafeRedirectTo(redirectTo)) {
            setAsyncError(new Error('unsafe_redirect_to'));
            return;
          }
          await waitForMinLoaderVisible(startedAt);
          if (cancelled) return;
          navigateToUrl(redirectTo);
          return;
        }
        setInfo(data);
      } catch (e) {
        if (cancelled) return;
        setAsyncError(e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [logout_challenge]);

  const handleCancel = () => {
    // 死んだ Hydra challenge URL に戻らないよう、認証後ホームへ遷移する。
    navigateToUrl('/');
  };

  if (error) {
    const fallbackKey =
      error instanceof OAuth2ApiError ? undefined : 'invalidRequest';
    return (
      <OAuth2Screen>
        <OAuth2ErrorCard error={error} fallbackKey={fallbackKey} />
      </OAuth2Screen>
    );
  }

  if (!info) {
    return (
      <OAuth2Screen>
        <OAuth2ProgressScreen title={t('progress.signingOut')} />
      </OAuth2Screen>
    );
  }

  const clientName = info.client?.clientName ?? info.client?.clientId ?? null;
  const confirmMessage = clientName
    ? t('logout.confirmMessage', { clientName })
    : t('logout.confirmMessageNoClient');

  return (
    <OAuth2Screen>
      <OAuth2Card
        header={
          <Text variant="h2" className="text-neutral-on-fill">
            {t('logout.title')}
          </Text>
        }
      >
        <VStack className="gap-6">
          <Text variant="p" className="text-neutral-on-fill">
            {confirmMessage}
          </Text>
          <HStack className="justify-end gap-3">
            <ActionButton
              variant="outline"
              onClick={handleCancel}
              disabled={submitting}
            >
              {tc('action.cancel')}
            </ActionButton>
            <ActionButton
              onClick={() => void handleConfirm()}
              isLoading={submitting}
            >
              {t('logout.actions.confirm')}
            </ActionButton>
          </HStack>
        </VStack>
      </OAuth2Card>
    </OAuth2Screen>
  );
}
